import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@docorga-v1/docorga-angular';
import { Docorga } from '@docorga-v1/docorga-common';

@Injectable()
export class AnonymousResource {
  private baseUri = '/anonymous';

  constructor(private ApiService: ApiService) {}

  // GET
  getPraticien(params: {
    professionalId: Docorga.interfaces.naturalTypes.NProfessionalObjectId;
  }): Observable<Docorga.interfaces.professional.IProfessionalPRLResult> {
    return this.ApiService.get(`${this.baseUri}/professionals/${params.professionalId}`);
  }

  findProfessionalsByQuery(params: {
    query: string;
  }): Observable<{ total_count: number; items: Array<Partial<Docorga.interfaces.professional.IProfessionalJson>> }> {
    return this.ApiService.get(`${this.baseUri}/professionals`, params);
  }

  findPrlProfessionalsByQuery(params: { query: string }): Observable<{ total_count: number; items: Array<any> }> {
    return this.ApiService.get(`${this.baseUri}/prlprofessionals`, params);
  }

  checkCode(professionalId: string, code: string): Observable<boolean> {
    return this.ApiService.get(`${this.baseUri}/professionals/${professionalId}/code`, { code });
  }

  search(query: string) {
    return this.ApiService.get(`${this.baseUri}/professionals`, { query });
  }

  findWaitingListProfessionalsByQuery(params: {
    query: string;
    expertizeId?: string;
  }): Observable<{ total_count: number; items: Array<Partial<Docorga.interfaces.professional.IProfessionalJson>> }> {
    return this.ApiService.get(`${this.baseUri}/professionals/waitingList`, params);
  }

  getAvailableTimeslots(params: {
    professionalId: string;
    locationId: string;
    reasonId: string;
    isPcoReason: boolean;
    start: number;
    end: number;
  }): Observable<Array<Partial<Docorga.interfaces.timeslot.ITimeslotJson>>> {
    return this.ApiService.get(`${this.baseUri}/professionals/${params.professionalId}/timeslots`, params);
  }

  getNextAvailableTimeslot(params: {
    professionalId: string;
    locationId: string;
    isPcoReason: boolean;
    reasonId: string;
    startTime: number;
  }): Observable<{ nextAvailability: Date | null }> {
    return this.ApiService.get(`${this.baseUri}/professionals/${params.professionalId}/nextTimeslot`, params);
  }

  findExpertizes(): Observable<Array<Docorga.interfaces.expertize.IExpertizeJson>> {
    return this.ApiService.get(`${this.baseUri}/expertizes`);
  }

  getAppointmentData(params: { appointmentKey: string }): Observable<{
    professional: {
      contact: Partial<Docorga.interfaces.professional.IProfessionalJson>;
      expertize: string | null;
    };
    location: Partial<Docorga.interfaces.location.ILocationJson>;
    reason: Partial<Docorga.interfaces.reason.IReasonJson>;
    patient: Partial<Docorga.interfaces.patientCard.IPatientCardJson>;
    appointment: Docorga.interfaces.event.IEventFullJson;
    summaryPage?: {
      displayReasonInfo: boolean;
      displayLocationInfo: boolean;
      displayMail: boolean;
      displayPhone: boolean;
      displayCellPhone: boolean;
    };
  }> {
    return this.ApiService.get(`${this.baseUri}/appointment/${params.appointmentKey}`);
  }

  getInvoiceByConversation(params: {
    messageId: string;
    memberId: string;
    key: string;
  }): Observable<Docorga.interfaces.invoice.IInvoiceForConversation> {
    return this.ApiService.get(`${this.baseUri}/invoices/byConversation`, params);
  }

  findInvoicesForPRLConversation(params: {
    conversationId: Docorga.interfaces.naturalTypes.NConversationObjectId;
    key: string;
    memberId: Docorga.interfaces.naturalTypes.NMemberObjectId;
  }): Observable<Array<Partial<Docorga.interfaces.invoice.IInvoicePublicJson>>> {
    return this.ApiService.get(`${this.baseUri}/invoices`, params);
  }

  getConversationIdByKey(params: { memberId: string; key: string }): Observable<string> {
    return this.ApiService.get(`${this.baseUri}/conversations`, params);
  }

  getConversationByKey(params: {
    conversationId: Docorga.interfaces.naturalTypes.NConversationObjectId;
    memberId: Docorga.interfaces.naturalTypes.NMemberObjectId;
    key: string;
  }): Observable<
    | { isSuccess: true; conversation: Partial<Docorga.interfaces.conversation.IConversationExtendedJson> }
    | { isSuccess: false; errorCode: string }
  > {
    return this.ApiService.get(`${this.baseUri}/conversations/${params.conversationId}/getConversationByKey`, params);
  }

  getQuotationByConversation(params: { messageId: string; memberId: string; key: string }): Observable<
    | {
        isSuccess: true;
        quotation: Partial<Docorga.interfaces.quotation.IQuotationJson>;
        professional: Partial<Docorga.interfaces.professional.IProfessionalJson>;
        messageId: string;
        memberId: string;
        key: string;
      }
    | {
        isSuccess: false;
        errorCode: string;
      }
  > {
    return this.ApiService.get(`${this.baseUri}/quotations/byConversation`, params);
  }

  findQuotationsForPRLConversation(params: {
    conversationId: Docorga.interfaces.naturalTypes.NConversationObjectId;
    key: string;
    memberId: Docorga.interfaces.naturalTypes.NMemberObjectId;
  }): Observable<Array<Partial<Docorga.interfaces.quotation.IQuotationJson>>> {
    return this.ApiService.get(`${this.baseUri}/quotations`, params);
  }

  findFilesForPRLConversation(params: {
    conversationId: Docorga.interfaces.naturalTypes.NConversationObjectId;
    key: string;
    memberId: Docorga.interfaces.naturalTypes.NMemberObjectId;
  }): Observable<Array<Docorga.interfaces.file.IFileJson>> {
    return this.ApiService.get(`${this.baseUri}/files`, params);
  }

  findConversationMembers(params: {
    conversationId: Docorga.interfaces.naturalTypes.NConversationObjectId;
    memberId: Docorga.interfaces.naturalTypes.NMemberObjectId;
    key: string;
  }): Observable<Array<Docorga.interfaces.member.IMemberJson>> {
    return this.ApiService.get(`${this.baseUri}/members/byConversation`, params);
  }

  findMessagesByConversation(params: {
    conversationId: Docorga.interfaces.naturalTypes.NConversationObjectId;
    key: string;
    memberId: Docorga.interfaces.naturalTypes.NMemberObjectId;
  }): Observable<Array<Docorga.interfaces.message.IMessageJson>> {
    return this.ApiService.get(`${this.baseUri}/messages`, params);
  }

  getPrebook(params: { prebookId: string }): Observable<Docorga.interfaces.prebook.IPrebookExtended> {
    return this.ApiService.get(`${this.baseUri}/prebooks/${params.prebookId}`);
  }

  // POST
  createActionSpy(params: { kind: string; state: string }): Observable<void> {
    return this.ApiService.post(`${this.baseUri}/actionSpies/create`, params);
  }

  createCheckoutSession(params: {
    invoiceId: Docorga.interfaces.naturalTypes.NInvoiceObjectId;
    messageId: string;
    memberId: string;
    key: string;
  }): Observable<{ sessionUrl: string }> {
    return this.ApiService.post(`${this.baseUri}/invoices/${params.invoiceId}/createCheckoutSession`, params);
  }

  createMessageFromAnonymous(params: {
    key: string;
    memberId: Docorga.interfaces.naturalTypes.NMemberObjectId;
    messageBody: Docorga.interfaces.message.IMessageCreateFromPRLJson;
  }): Observable<void> {
    return this.ApiService.post(`${this.baseUri}/messages`, params);
  }

  createPrebook(params: {
    prebookBody: Docorga.interfaces.prebook.IPrebookCreateJson;
  }): Observable<Docorga.interfaces.prebook.IPrebookJson> {
    return this.ApiService.post(`${this.baseUri}/prebooks`, params);
  }

  // PUT
  unsubscribeFromCancellationlAlerts(tokenId: string): Observable<boolean> {
    return this.ApiService.put(`${this.baseUri}/unsubscribe/cancellationAlerts`, { tokenId });
  }

  updateConversationLastDisplay(params: {
    memberId: Docorga.interfaces.naturalTypes.NMemberObjectId;
    conversationId: Docorga.interfaces.naturalTypes.NConversationObjectId;
    key: string;
  }): Observable<void> {
    return this.ApiService.put(`${this.baseUri}/members/${params.memberId}/updateLastDisplay`, params);
  }

  updateFirstModalSeen(params: {
    memberId: Docorga.interfaces.naturalTypes.NMemberObjectId;
    key: string;
  }): Observable<void> {
    return this.ApiService.put(`${this.baseUri}/members/${params.memberId}/updateFirstModalSeen`, params);
  }

  updateMemberShareStatus(params: {
    conversationId: Docorga.interfaces.naturalTypes.NConversationObjectId;
    key: string;
    memberId: Docorga.interfaces.naturalTypes.NMemberObjectId;
    messageId: Docorga.interfaces.naturalTypes.NMessageObjectId;
  }): Observable<void> {
    return this.ApiService.put(`${this.baseUri}/messages/updateMemberStatus`, params);
  }

  anonymousRevokeShare(params: {
    conversationId: Docorga.interfaces.naturalTypes.NConversationObjectId;
    key: string;
    memberId: Docorga.interfaces.naturalTypes.NMemberObjectId;
    messageId: Docorga.interfaces.naturalTypes.NMessageObjectId;
  }): Observable<void> {
    return this.ApiService.put(`${this.baseUri}/messages/revokeShare`, params);
  }
}
